import React from "react";
import classes from "./Footer.module.scss";
import clutchIcon from "../../media/footer/clutch.webp";
import linkedinIcon from "../../media/footer/linkedin.png";
import clockIconFirst from "../../media/footer/clock-0.svg";
import clockIconSecond from "../../media/footer/clock-1.svg";
import locationIcon from "../../media/footer/location.svg";
import callIcon from "../../media/footer/call.svg";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";

interface IBlock {
  address: string;
  days: string;
  id: number;
  number: string;
  time: string;
  utc: string;
}

export type FooterLink = {
  title: string;
  link: string;
  isLink: boolean;
  id: number;
};

export type FooterLinkBlock = {
  title: string;
  links: FooterLink[];
};

const Footer = () => {
  const { t } = useTranslation();
  const blocksData: IBlock[] = [
    {
      address: t("footer.offices.0.address"),
      days: t("footer.offices.0.days"),
      id: 0,
      number: "+1 (585) 445-1323",
      time: "9:00 - 18:00",
      utc: "UTC - 8",
    },
    {
      address: t("footer.offices.1.address"),
      days: t("footer.offices.1.days"),
      id: 1,
      number: "+38 (044) 333-92-74",
      time: "9:00 - 18:00",
      utc: "UTC + 2",
    },
  ];

  const socialMediaList = [
    {
      icon: clutchIcon,
      name: "clutch",
      link: "https://clutch.co/profile/goteco",
    },
    {
      icon: linkedinIcon,
      name: "linkedin",
      link: "https://www.linkedin.com/company/gote-co/",
    },
  ];

  const clockIcons: Record<number, string> = {
    0: clockIconFirst,
    1: clockIconSecond,
  };


  const footerTopLinks: FooterLink[] = [
    { title: t('footer.footerTopLinks.0.title'), link: "who-we-are", isLink: true, id: 0 },
    { title: t('footer.footerTopLinks.1.title'), link: "contacts", isLink: true, id: 1 },
    { title: t('footer.footerTopLinks.2.title'), link: "partnership", isLink: true, id: 2 },
    { title: t('footer.footerTopLinks.3.title'), link: "blogs", isLink: true, id: 4 },
  ];

  const footerLinks: FooterLinkBlock[] = [
    {
      title: t('footer.servicesTitle'),
      links: [
        {
          title: t('footer.servicesLinks.0.title'),
          link: "/services/strategic-it-consulting",
          isLink: true,
          id: 0,
        },
        {
          title: t('footer.servicesLinks.1.title'),
          link: "/services/software-development-services",
          isLink: true,
          id: 1,
        },
        {
          title: t('footer.servicesLinks.2.title'),
          link: "/services/software-implementation-and-support",
          isLink: true,
          id: 2,
        },
      ],
    },
    {
      title: t('footer.projectsTitle'),
      links: [
        { title: t('footer.projectsLinks.0.title'),          link: "/case-study/cybertracker", isLink: true, id: 5 },
        { title: t('footer.projectsLinks.1.title'),  link: "/case-study/1551", isLink: true, id: 6 },
        { title: t('footer.projectsLinks.2.title'),     link: "/case-study/e-road", isLink: true, id: 7 },
        { title: t('footer.projectsLinks.3.title'),      link: "/case-study/e-cherha", isLink: true, id: 8 },
        { title: t('footer.projectsLinks.4.title'),      link: "/case-study/go-go-bag", isLink: true, id: 27 },
        { title: t('footer.projectsLinks.5.title'),           link: "/case-study/syvi", isLink: true, id: 28 },
      ],
    },
    {
      title: t('footer.industriesTitle'),
      links: [
        { title: t('footer.industriesLinks.0.title'), link: "/industries/logistics-and-supply-chains", isLink: true, id: 9 },
        { title: t('footer.industriesLinks.1.title'), link: "/industries/healthcare", isLink: true, id: 10 },
        { title: t('footer.industriesLinks.2.title'), link: "/industries/finance-and-banking", isLink: true, id: 11 },
      ],
    },
    {
      title: t('footer.solutionsTitle'),
      links: [
        { title: t('footer.solutionsLinks.0.title'), link: "/solutions/crm-for-logistics", isLink: true, id: 14 },
        { title: t('footer.solutionsLinks.1.title'), link: "/solutions/project-management-system", isLink: true, id: 15 },
        { title: t('footer.solutionsLinks.2.title'), link: "/solutions/erp-for-financial-systems", isLink: true, id: 16 },
        { title: t('footer.solutionsLinks.3.title'), link: "/solutions/smart-warehouse-management", isLink: true, id: 17 },
      ],
    },
  ];

  const footerBottomLinks: FooterLink[] = [
    { title: t('footer.footerBottomLink'), isLink: true, link: "/privacy-policy", id: 0 },
  ];

  const formatPhone = (phone: string) =>
      phone.replace(/\s|\(|\)|-/g, "");

  return (
      <footer className={classes.footer}>
        <div className={classes.left}>
          <div className={classes.info}>
            <div className={classes["message-email-block"]}>
              <h2 className={classes.header}>{t('footer.footerTopText')}</h2>
              <p className={classes.email}>
                <a href="mailto:info@go-te.co">info@go-te.co</a>
              </p>
            </div>

            <div className={classes["icons-list"]}>
              {socialMediaList.map((sm) => (
                  <a href={sm.link} target="_blank" rel="noopener noreferrer" key={sm.name}>
                    <img src={sm.icon} alt={sm.name} className={classes.icon} />
                  </a>
              ))}
            </div>
          </div>

          <div className={classes.blocks}>
            {blocksData.map((block) => (
                <div className={classes["block-wrap"]} key={block.id}>
                  <div className={classes.block}>
                    <div className={classes.heading}>
                      <img src={clockIcons[block.id]} alt="Clock" />
                      <div className={classes.info}>
                        <p className={classes.days}>{block.days}</p>
                        <div className={classes["time-block"]}>
                          <p className={classes.time}>{block.time}</p>
                          <p className={classes.utc}>{block.utc}</p>
                        </div>
                      </div>
                    </div>

                    <div className={classes.address}>
                      <img src={locationIcon} alt="Location" />
                      <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              block.address
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                      >
                        {block.address}
                      </a>
                    </div>

                    <div className={classes.call}>
                      <img src={callIcon} alt="Call" />
                      <a href={`tel:${formatPhone(block.number)}`}>
                        {block.number}
                      </a>
                    </div>
                  </div>
                </div>
            ))}
          </div>
        </div>

        <div className={classes.right}>
          <div className={classes.menu}>
            {footerTopLinks.map(({ title, link }) => (
                <Link to={link} className={classes["menu-item"]} key={title}>
                  {title}
                </Link>
            ))}
          </div>

          <div className={classes.divider}></div>

          <div className={classes["links-container"]}>
            {footerLinks.map((linkBlock) => (
                <div className={classes["links-block"]} key={linkBlock.title}>
                  <h4 className={classes["links-block--header"]}>
                    {linkBlock.title}
                  </h4>
                  {linkBlock.links.map(({ link, isLink, title, id }) =>
                      isLink ? (
                          <Link className={classes.link} to={link} key={id}>
                            {title}
                          </Link>
                      ) : (
                          <div className={classes.link} key={id}>
                            {title}
                          </div>
                      )
                  )}
                </div>
            ))}
          </div>

          <div className={classes.divider}></div>

          <div className={classes["privacy-links-container"]}>
            {footerBottomLinks.map(({ title, isLink, link, id }) =>
                isLink ? (
                    <Link className={classes.link} to={link} key={id}>
                      {title}
                    </Link>
                ) : (
                    <div className={classes.link} key={id}>
                      {title}
                    </div>
                )
            )}
          </div>

          <span className={classes.copyright}>
          {t('footer.footerBottomText')}
        </span>
        </div>
      </footer>
  );
};

export default Footer;